import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../../firebase/config";
import { collection, getDocs, query, orderBy } from "firebase/firestore";
import { db } from "../../firebase/config";
import AddAdmin from "../../components/AddAdmin";

const AdminManagement = () => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAdminStatus = async () => {
      const user = auth.currentUser;
      if (!user) {
        navigate("/admin/login");
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult();

        if (idTokenResult.claims.role === 'admin') {
          setAdmin({
            uid: user.uid,
            email: user.email,
            name: user.displayName,
            role: 'admin',
            permissions: idTokenResult.claims.permissions || []
          });
        } else {
          navigate("/admin/login");
        }
      } catch (error) {
        console.error("Error checking admin status:", error);
        navigate("/admin/login");
      } finally {
        setLoading(false);
      }
    };

    checkAdminStatus();
  }, [navigate]);

  const fetchAdmins = async () => {
    try {
      const adminsQuery = query(collection(db, "admin"), orderBy("createdAt", "desc"));
      const snapshot = await getDocs(adminsQuery);
      
      const adminsList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate?.() || new Date(doc.data().createdAt)
      }));
      
      setAdmins(adminsList);
    } catch (error) {
      console.error("Error fetching admins:", error);
    } finally {
      setLoadingAdmins(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/admin/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Intl.DateTimeFormat('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-700 font-medium">Loading Admin Panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/admin/dashboard')}
                className="text-green-600 hover:text-green-700 font-medium"
              >
                ← Back to Dashboard
              </button>
              <div>
                <h1 className="text-2xl font-bold text-green-700">JR Home Made Foods</h1>
                <p className="text-gray-600">Admin Management</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <span className="text-gray-700">Welcome, {admin?.name}</span>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-all"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-green-700 mb-6">👑 Admin Management</h1>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Add New Admin Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-green-100">
            <h2 className="text-xl font-bold text-green-700 mb-4">➕ Add New Admin</h2>
            <p className="text-gray-600 mb-6">
              Add new administrators to the system. They will be able to access the admin panel and manage the platform.
            </p>
            <AddAdmin onAdminAdded={fetchAdmins} />
          </div>

          {/* Existing Admins Section */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-green-100">
            <h2 className="text-xl font-bold text-green-700 mb-4">👥 Existing Admins</h2>
            
            {loadingAdmins ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto mb-4"></div>
                <p className="text-gray-500">Loading admins...</p>
              </div>
            ) : admins.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <p>No admins found</p>
              </div>
            ) : (
              <div className="space-y-4">
                {admins.map((adminUser) => (
                  <div key={adminUser.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-gray-800">{adminUser.name}</h3>
                        <p className="text-gray-600 text-sm">{adminUser.email}</p>
                        <p className="text-gray-500 text-xs">
                          Added: {formatDate(adminUser.createdAt)}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2">
                        <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-medium">
                          {adminUser.role}
                        </span>
                        {adminUser.uid === admin?.uid && (
                          <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full font-medium">
                            You
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Admin Permissions Info */}
        <div className="mt-8 bg-blue-50 rounded-xl p-6 border border-blue-200">
          <h2 className="text-lg font-bold text-blue-700 mb-3">ℹ️ Admin Permissions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-blue-800">
            <div>
              <h3 className="font-semibold mb-2">What admins can do:</h3>
              <ul className="space-y-1">
                <li>• Access admin dashboard</li>
                <li>• Manage subscriptions and deliveries</li>
                <li>• View customer data and analytics</li>
                <li>• Assign delivery partners</li>
                <li>• Manage coupons and promotions</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Security notes:</h3>
              <ul className="space-y-1">
                <li>• Only existing admins can add new admins</li>
                <li>• Admin roles are stored securely in Firestore</li>
                <li>• Custom claims are set for backend security</li>
                <li>• All admin actions are logged</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminManagement; 